import { createClient, FunctionsHttpError } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL;
const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!url || !publishableKey) {
  throw new Error(
    "Missing VITE_SUPABASE_URL or VITE_SUPABASE_PUBLISHABLE_KEY — check your .env.local"
  );
}

export const supabase = createClient(url, publishableKey);

// supabase-js never surfaces an edge function's own JSON error body through
// `error.message` — on a non-2xx response `error` is a FunctionsHttpError
// whose `.message` is always the generic "Edge Function returned a non-2xx
// status code", and the real `{ error: "..." }` body only lives on
// `error.context` (the raw Response). Every function wrapper below routes
// through this so callers actually see the server's real message.
async function invokeFunction<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    let message = error instanceof Error ? error.message : "Erreur inconnue";
    if (error instanceof FunctionsHttpError) {
      try {
        const parsed = await error.context.json();
        if (parsed && typeof parsed === "object" && typeof parsed.error === "string") {
          message = parsed.error;
        }
      } catch {
        // response body wasn't JSON — keep the generic message
      }
    }
    throw new Error(message);
  }
  return data as T;
}

export interface DemoRequestInput {
  businessName: string;
  businessType: string;
  contactName: string;
  email: string;
  phone: string;
  message: string;
}

export async function submitDemoRequest(input: DemoRequestInput) {
  const { error } = await supabase.from("demo_requests").insert({
    business_name: input.businessName,
    business_type: input.businessType || null,
    contact_name: input.contactName,
    email: input.email,
    phone: input.phone || null,
    message: input.message || null,
  });

  if (error) throw error;
}

export type SignupPlan = "standard" | "pro";

export interface SignupMerchantInput {
  businessName: string;
  businessType: string;
  contactName: string;
  email: string;
  phone: string;
  password: string;
  plan: SignupPlan;
}

/** Self-serve merchant signup — creates the auth account and the merchant record together. */
export async function signupMerchant(input: SignupMerchantInput) {
  return invokeFunction<{ success: true; merchantId: string }>("signup-merchant", { ...input });
}

/** Reads a non-secret platform config value (e.g. the Stripe payment link). Returns null if unset. */
export async function getPublicSetting(key: string): Promise<string | null> {
  const { data } = await supabase.from("public_settings").select("value").eq("key", key).maybeSingle();
  return data?.value ?? null;
}

export type DemoRequestStatus = "nouveau" | "contacte" | "converti" | "perdu";

export interface DemoRequest {
  id: string;
  business_name: string;
  business_type: string | null;
  contact_name: string;
  email: string;
  phone: string | null;
  status: DemoRequestStatus;
  notes: string | null;
  created_at: string;
}

export type MerchantStatus = "prospect" | "actif" | "suspendu" | "resilie";

export const loyaltyTypes = ["tampons", "points", "cashback"] as const;
export type LoyaltyType = (typeof loyaltyTypes)[number];

export interface Merchant {
  id: string;
  business_name: string;
  business_type: string | null;
  contact_name: string;
  email: string;
  phone: string | null;
  status: MerchantStatus;
  monthly_fee: number | null;
  referred_by: string | null;
  referral_code: string;
  notes: string | null;
  converted_from_demo_request_id: string | null;
  user_id: string | null;
  logo_url: string | null;
  card_background_url: string | null;
  card_color: string;
  card_title: string | null;
  card_label: string | null;
  barcode_alt_text: string | null;
  icon_url: string | null;
  thumbnail_url: string | null;
  pass_expiration_days: number | null;
  pass_sharing_allowed: boolean;
  pass_description: string | null;
  loyalty_type: LoyaltyType;
  reward_label: string;
  cashback_rate: number;
  barcode_format: BarcodeFormat;
  relance_enabled: boolean;
  relance_days: number;
  relance_message: string;
  vip_silver_threshold: number;
  vip_gold_threshold: number;
  birthday_enabled: boolean;
  birthday_message: string;
  birthday_reward: number;
  customer_referral_enabled: boolean;
  customer_referral_reward: number;
  google_review_url: string | null;
  appointments_enabled: boolean;
  zero_trou_enabled: boolean;
  zero_trou_max_discount: number;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  weather_offers_enabled: boolean;
  weather_rain_message: string;
  weather_heat_message: string;
  weather_heat_threshold_c: number;
  dead_hours_enabled: boolean;
  dead_hours_max_discount: number;
  social_proof_enabled: boolean;
  weekly_digest_enabled: boolean;
  plan: "standard" | "pro";
  website_tagline: string | null;
  website_description: string | null;
  website_hours: string | null;
  website_instagram_url: string | null;
  website_facebook_url: string | null;
  website_contact_phone: string | null;
  website_contact_email: string | null;
  created_at: string;
}

export interface MerchantWebsitePublic {
  id: string;
  business_name: string;
  business_type: string | null;
  logo_url: string | null;
  card_color: string;
  address: string | null;
  appointments_enabled: boolean;
  google_review_url: string | null;
  website_tagline: string | null;
  website_description: string | null;
  website_hours: string | null;
  website_instagram_url: string | null;
  website_facebook_url: string | null;
  website_contact_phone: string | null;
  website_contact_email: string | null;
}

export interface MerchantPartnership {
  id: string;
  merchant_id: string;
  partner_merchant_id: string;
  partner_business_name: string;
  welcome_message: string;
  active: boolean;
  created_at: string;
}

export async function findMerchantByReferralCode(referralCode: string) {
  const { data, error } = await supabase.rpc("find_merchant_by_referral_code", {
    p_referral_code: referralCode,
  });
  if (error) throw error;
  const match = data?.[0] as { id: string; business_name: string } | undefined;
  if (!match) throw new Error("Aucun commerce trouvé avec ce code");
  return match;
}

export interface MerchantReferral {
  id: string;
  business_name: string;
  status: MerchantStatus;
}

export type VipTier = "bronze" | "argent" | "or";

export function vipTierFor(totalScans: number, silverThreshold: number, goldThreshold: number): VipTier {
  if (totalScans >= goldThreshold) return "or";
  if (totalScans >= silverThreshold) return "argent";
  return "bronze";
}

export const cardColorPresets = ["orange", "green", "dark"] as const;
export type CardColorPreset = (typeof cardColorPresets)[number];

// Quick-pick swatches shown in the personalization UI: the 3 legacy preset
// names first (so they keep resolving to the branded coral accent via
// accentFor() in WheelGame/RdvPage/MerchantWebsite), then 7 plain hex
// colors that go through cardTheme()'s generic luminance-based contrast
// path. The unlimited custom-color picker covers everything beyond these.
export const quickCardColors: string[] = [
  ...cardColorPresets,
  "#e63946",
  "#3a86ff",
  "#8338ec",
  "#ffbe0b",
  "#2d6a4f",
  "#e07a5f",
  "#495057",
];

const CARD_COLOR_PRESET_THEME: Record<CardColorPreset, { bg: string; fg: string; fgSoft: string }> = {
  orange: { bg: "#ffa94d", fg: "#14142b", fgSoft: "rgba(20,20,43,0.65)" },
  green: { bg: "#1fbf87", fg: "#14142b", fgSoft: "rgba(20,20,43,0.65)" },
  dark: { bg: "#14142b", fg: "#ffffff", fgSoft: "rgba(255,255,255,0.65)" },
};

/**
 * `merchants.card_color` holds either one of the 3 legacy preset names or
 * (since the color picker went unlimited) a raw "#rrggbb" hex. Either way,
 * this resolves the actual background plus a WCAG-safe foreground: for a
 * custom hex, text color is picked from its perceived luminance rather than
 * defaulting to a fixed ink/white pair that can fail contrast on an
 * arbitrary background (the exact bug the 3 fixed presets were built to
 * avoid — see the comment in MerchantSpace.tsx's CARD_PREVIEW_THEME).
 */
export function cardTheme(cardColor: string): { bg: string; fg: string; fgSoft: string } {
  const preset = CARD_COLOR_PRESET_THEME[cardColor as CardColorPreset];
  if (preset) return preset;
  if (/^#[0-9a-f]{6}$/i.test(cardColor)) {
    const r = parseInt(cardColor.slice(1, 3), 16);
    const g = parseInt(cardColor.slice(3, 5), 16);
    const b = parseInt(cardColor.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    const dark = luminance > 0.6;
    return {
      bg: cardColor,
      fg: dark ? "#14142b" : "#ffffff",
      fgSoft: dark ? "rgba(20,20,43,0.65)" : "rgba(255,255,255,0.65)",
    };
  }
  return CARD_COLOR_PRESET_THEME.orange;
}

// Values accepted by the WalletWallet pass API's barcodeFormat field —
// confirmed live against create_pass/get_pass_body (2026-09-12): all 4
// formats in the tool's enum are actually accepted, not just QR/PDF417.
export const barcodeFormats = ["QR", "PDF417", "Aztec", "Code128"] as const;
export type BarcodeFormat = (typeof barcodeFormats)[number];

export type WalletType = "ios" | "android" | "none";

export interface Customer {
  id: string;
  merchant_id: string;
  name: string;
  email: string | null;
  phone: string | null;
  wallet_type: WalletType;
  points: number;
  total_spent: number;
  cashback_balance: number;
  last_visit: string | null;
  wallet_pass_serial: string | null;
  wallet_share_url: string | null;
  stamp_goal: number;
  active: boolean;
  total_scans: number;
  location_id: string | null;
  birthday: string | null;
  last_birthday_reward_year: number | null;
  referred_by_customer_id: string | null;
  notes: string | null;
  tags: string[];
  created_at: string;
}

export type StampSource = "manual" | "referral" | "birthday" | "relance";

export interface StampEvent {
  id: string;
  merchant_id: string;
  customer_id: string;
  source: StampSource;
  amount: number | null;
  points_before: number | null;
  undone: boolean;
  staff_name: string | null;
  created_at: string;
}

/** Reverses a mistaken stamp, restoring the customer's exact prior balance. */
export async function undoStamp(stampEventId: string) {
  const { error } = await supabase.rpc("undo_stamp", { p_stamp_event_id: stampEventId });
  if (error) throw error;
}

/**
 * Manually corrects a customer's balance (e.g. an old mis-scan, or comping a
 * complaint) outside the scan flow. A reason is mandatory server-side and the
 * change is logged to stamp_events like any other balance mutation.
 */
export async function adjustCustomerBalance(
  customerId: string,
  pointsDelta: number,
  cashbackDelta: number,
  note: string
) {
  const { data, error } = await supabase
    .rpc("adjust_customer_balance", {
      p_customer_id: customerId,
      p_points_delta: pointsDelta,
      p_cashback_delta: cashbackDelta,
      p_note: note,
    })
    .single();
  if (error) throw error;
  return data as { points: number; cashback_balance: number };
}

/** Sends the weekly digest email immediately, so a merchant can check it before waiting for Monday. */
export async function sendDigestPreview(merchantId: string) {
  return invokeFunction<{ success: true; sentTo: string }>("send-digest-preview", { merchantId });
}

export interface MerchantLocation {
  id: string;
  merchant_id: string;
  name: string;
  address: string | null;
  created_at: string;
}

export type ScheduledNotificationStatus = "pending" | "sent" | "failed" | "cancelled";
export const notificationRecurrences = ["none", "daily", "weekly"] as const;
export type NotificationRecurrence = (typeof notificationRecurrences)[number];

export interface ScheduledNotification {
  id: string;
  merchant_id: string;
  message: string;
  scheduled_at: string;
  status: ScheduledNotificationStatus;
  recurrence: NotificationRecurrence;
  recipient_count: number | null;
  notified_devices: number | null;
  failed_sends: number | null;
  sent_at: string | null;
  last_sent_at: string | null;
  created_at: string;
}

export interface NotificationTemplate {
  id: string;
  merchant_id: string;
  label: string;
  message: string;
  created_at: string;
}

export async function createLoyaltyPass(input: {
  merchantId: string;
  name: string;
  email: string;
  phone: string;
  locationId?: string;
  birthday: string;
  referredByCustomerId?: string;
  termsAccepted: boolean;
  marketingConsent: boolean;
}) {
  return invokeFunction<{
    success: true;
    customerId: string;
    appleUrl: string | null;
    googleSaveUrl: string | null;
    shareUrl: string | null;
  }>("create-loyalty-pass", input);
}

export async function addStamp(customerId: string, amount?: number) {
  return invokeFunction<{
    success: true;
    points: number;
    cashbackBalance: number;
    rewardEarned: boolean;
    walletPushFailed: boolean;
  }>("add-stamp", { customerId, amount });
}

export interface CustomerSearchResult {
  id: string;
  name: string;
  phone: string | null;
  points: number;
  stamp_goal: number;
  cashback_balance: number;
  total_scans: number;
}

export async function searchCustomers(merchantId: string, query: string) {
  return invokeFunction<{ success: true; customers: CustomerSearchResult[] }>("search-customers", {
    merchantId,
    query,
  });
}

export async function sendNotification(input: { merchantId: string; message: string; customerIds?: string[] }) {
  return invokeFunction<{
    success: true;
    recipientCount: number;
    notifiedDevices: number;
    failedSends: number;
  }>("send-notification", input);
}

export async function createMerchantAccount(input: {
  merchantId: string;
  email: string;
  password: string;
}) {
  return invokeFunction<{ success: true; email: string }>("create-merchant-account", input);
}

export interface StaffMember {
  id: string;
  merchant_id: string;
  name: string;
  active: boolean;
  created_at: string;
}

export async function createStaffAccount(input: { name: string; email: string; password: string; merchantId?: string }) {
  return invokeFunction<{ success: true; email: string }>("create-staff-account", input);
}

export async function geocodeAddress(address: string) {
  return invokeFunction<{ success: true; latitude: number; longitude: number; formattedAddress: string }>(
    "geocode-address",
    { address }
  );
}

export interface WheelPrize {
  id: string;
  merchant_id: string;
  label: string;
  weight: number;
  active: boolean;
  created_at: string;
}

export interface WheelConfig {
  merchant_id: string;
  active: boolean;
  require_review: boolean;
  updated_at: string;
}

export interface WheelSpin {
  id: string;
  merchant_id: string;
  prize_id: string | null;
  prize_label: string;
  name: string;
  email: string;
  redemption_code: string;
  redeemed: boolean;
  redeemed_at: string | null;
  review_clicked: boolean;
  created_at: string;
}

export interface WheelPublicInfo {
  merchant_id: string;
  business_name: string;
  logo_url: string | null;
  card_color: string;
  active: boolean;
  google_review_url: string | null;
  require_review: boolean;
}

export interface WheelPublicPrize {
  merchant_id: string;
  id: string;
  label: string;
}

export async function spinWheel(input: {
  merchantId: string;
  name: string;
  email: string;
  reviewClicked?: boolean;
}) {
  return invokeFunction<{ success: true; prizeLabel: string; redemptionCode: string }>("spin-wheel", input);
}

export interface MerchantPublicInfo {
  id: string;
  business_name: string;
  business_type: string | null;
  google_review_url: string | null;
  customer_referral_enabled: boolean;
}

export interface CustomerFeedback {
  id: string;
  merchant_id: string;
  rating: number;
  message: string | null;
  redirected_to_google: boolean;
  created_at: string;
}

export type SupportTicketStatus = "open" | "in_progress" | "resolved";

export interface SupportTicket {
  id: string;
  merchant_id: string;
  subject: string;
  status: SupportTicketStatus;
  created_at: string;
}

export interface SupportTicketMessage {
  id: string;
  ticket_id: string;
  author_type: "merchant" | "admin";
  message: string;
  created_at: string;
}

export interface PlatformAnnouncement {
  id: string;
  message: string;
  active: boolean;
  created_at: string;
}

export async function submitFeedback(input: { merchantId: string; rating: number; message?: string; redirectedToGoogle: boolean }) {
  return invokeFunction<{ success: true }>("submit-feedback", input);
}

export interface AppointmentSlot {
  id: string;
  merchant_id: string;
  location_id: string | null;
  starts_at: string;
  duration_minutes: number;
  capacity: number;
  booked_count: number;
  created_at: string;
}

export interface Appointment {
  id: string;
  merchant_id: string;
  slot_id: string | null;
  name: string;
  email: string;
  phone: string | null;
  status: "confirmed" | "cancelled" | "no_show";
  created_at: string;
}

export interface AppointmentPublicSlot {
  id: string;
  merchant_id: string;
  location_id: string | null;
  starts_at: string;
  duration_minutes: number;
  capacity: number;
  booked_count: number;
  business_name: string;
  logo_url: string | null;
  card_color: string;
}

export async function bookAppointment(input: { merchantId: string; slotId: string; name: string; email: string; phone?: string }) {
  return invokeFunction<{ success: true; appointmentId: string; customerId: string | null }>("book-appointment", input);
}

export async function cancelAppointment(appointmentId: string) {
  const { error } = await supabase.rpc("cancel_appointment", { p_appointment_id: appointmentId });
  if (error) throw error;
}

export interface ZeroTrouOffer {
  id: string;
  merchant_id: string;
  slot_id: string | null;
  customer_id: string;
  discount_percent: number;
  code: string;
  starts_at: string;
  expires_at: string;
  redeemed: boolean;
  redeemed_at: string | null;
  created_at: string;
}

export interface CustomerAccount {
  business_name: string;
  loyalty_type: LoyaltyType;
  points: number;
  cashback_balance: number;
  stamp_goal: number;
  reward_label: string;
  total_scans: number;
  last_visit: string | null;
  vip_silver_threshold: number;
  vip_gold_threshold: number;
}

export interface StampHistoryEntry {
  source: StampSource;
  amount: number | null;
  created_at: string;
}

export interface CustomerAppointment {
  appointment_id: string;
  starts_at: string;
  duration_minutes: number;
  business_name: string;
}

export async function getMyAccount(merchantId: string, customerId: string) {
  return invokeFunction<{
    success: true;
    account: CustomerAccount;
    history: StampHistoryEntry[];
    appointments: CustomerAppointment[];
  }>("get-my-account", { merchantId, customerId });
}

export async function cancelMyAppointment(appointmentId: string, customerId: string) {
  return invokeFunction<{ success: true }>("cancel-my-appointment", { appointmentId, customerId });
}

export async function exportMyData(merchantId: string, customerId: string) {
  return invokeFunction<{ success: true; data: unknown }>("manage-my-account", {
    action: "export",
    merchantId,
    customerId,
  });
}

export async function deleteMyAccount(merchantId: string, customerId: string) {
  return invokeFunction<{ success: true }>("manage-my-account", {
    action: "delete",
    merchantId,
    customerId,
  });
}

export function checkinStorageKey(merchantId: string) {
  return `fidelo_customer_${merchantId}`;
}

export function downloadCsv(fileName: string, headers: string[], rows: (string | number)[][]) {
  function escapeCell(cell: string | number) {
    let text = String(cell);
    // Neutralize spreadsheet formula injection: a cell value coming from
    // user-supplied data (e.g. a customer name) that starts with =, +, -,
    // @ or a tab is interpreted as a formula by Excel/Sheets when opened.
    if (/^[=+\-@\t]/.test(text)) text = `'${text}`;
    return /[",\n;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  }
  const csv = [headers, ...rows].map((row) => row.map(escapeCell).join(";")).join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${fileName}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
